import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { previsualizarBajaCliente } from '../services/clienteService';

const formatearFechaTurno = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha + 'T00:00:00');
    return Number.isNaN(d.getTime()) ? fecha : d.toLocaleDateString('es-AR');
};

/**
 * Va DENTRO del diálogo de "Confirmar Baja" (VistaDetalladaCliente.js y ListadoClientes.js):
 * qué trámites y turnos se cancelarían si se confirma. Se pide apenas se abre el diálogo (no
 * antes), para no pegarle al endpoint en cada ficha de cliente abierta.
 *
 * `onCargado(preview)` avisa al diálogo contenedor cuándo terminó de cargar (para deshabilitar
 * el botón de confirmar mientras no se sabe todavía qué se va a cancelar).
 */
export default function PreviewCancelacionBajaCliente({ cuil, onCargado }) {
    const [preview, setPreview] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!cuil) return;
        let cancelado = false;
        setPreview(null);
        setError(null);
        setCargando(true);
        previsualizarBajaCliente(cuil)
            .then((data) => {
                if (cancelado) return;
                setPreview(data);
                onCargado?.(data);
            })
            .catch(() => {
                if (cancelado) return;
                setError('No se pudo calcular qué trámites y turnos se cancelarían.');
                onCargado?.(null);
            })
            .finally(() => { if (!cancelado) setCargando(false); });
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cuil]);

    if (cargando) {
        return (
            <div className="d-flex align-items-center gap-2 text-muted my-2" style={{ fontSize: '0.88rem' }}>
                <CircularProgress size={16} /> Calculando qué trámites y turnos se cancelarían…
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
            </div>
        );
    }

    const tramites = preview?.tramites || [];
    const turnos = preview?.turnos || [];

    if (tramites.length === 0 && turnos.length === 0) {
        return (
            <div className="alert alert-light border mb-3" style={{ fontSize: '0.85rem' }}>
                No tiene trámites ni turnos en curso: la baja no cancela nada más.
            </div>
        );
    }

    return (
        <div className="border rounded-3 p-3 mb-3" style={{ background: '#fff7ed', borderColor: '#fde68a' }}>
            <h6 className="fw-bold mb-2" style={{ color: '#b45309', fontSize: '0.92rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Al confirmar se cancelan:
            </h6>

            {tramites.length > 0 && (
                <>
                    <p className="mb-1 fw-semibold text-muted" style={{ fontSize: '0.82rem' }}>
                        Trámites ({tramites.length})
                    </p>
                    <ul className="mb-2 ps-3" style={{ fontSize: '0.85rem' }}>
                        {tramites.map((t) => (
                            <li key={t.idTramite}>
                                #{t.idTramite} — <strong>{t.tipoTramite}</strong> ({t.nombreEstadoActual})
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {turnos.length > 0 && (
                <>
                    <p className="mb-1 fw-semibold text-muted" style={{ fontSize: '0.82rem' }}>
                        Turnos ({turnos.length})
                    </p>
                    <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
                        {turnos.map((t) => (
                            <li key={`${t.tipo}-${t.idTurno}`}>
                                {t.descripcion} — {formatearFechaTurno(t.fecha)}{t.hora ? ` ${t.hora.slice(0, 5)}hs` : ''}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
